// Rate plans list (SCR-008): name, status, what the markup applies to, markup with its margin,
// fee, minimum, exceptions and tiers, plus New plan for billing writers. "Customers on the plan"
// is left out — RatePlanList carries no such count and the screen must not cross-join every
// customer's assignments client-side.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { RouterContext } from "../../router/router";
import { SessionContext } from "../../session/session";
import { Icon } from "@nexus/ui";
import { Chip, EmptyState, PageHeader } from "@nexus/ui";
import { Link } from "../../ui/shell";
import { DataTable, Money, type Column } from "@nexus/ui";
import {
  compareRatePlanMarkup,
  compareRatePlanName,
  compareRatePlanStatus,
  loadRatePlans,
  ratePlanRow,
  type RatePlan,
  type RatePlanListState,
  type RatePlanRow,
} from "./model";

const COLUMNS: readonly Column<RatePlanRow>[] = [
  {
    key: "name",
    header: "Plan",
    cell: (r) => (
      <span class="stack-cell">
        <Link href={`/rate-plans/${r.id}`}>{r.name}</Link>
        {r.description && <span class="cell-sub plan-description">{r.description}</span>}
      </span>
    ),
    compare: compareRatePlanName,
  },
  { key: "status", header: "Status", help: "rateplan.status", cell: (r) => <Chip tone={r.statusTone}>{r.statusLabel}</Chip>, compare: compareRatePlanStatus },
  { key: "basis", header: "Markup applies to", help: "rateplan.markupBasis", cell: (r) => r.basisLabel },
  {
    key: "markup",
    header: "Markup",
    help: "common.markupAndMargin",
    numeric: true,
    cell: (r) => (
      <span class="stack-cell">
        <span>{r.markupLabel}</span>
        <span class="cell-sub plan-margin">{r.marginLabel}</span>
      </span>
    ),
    compare: compareRatePlanMarkup,
  },
  { key: "fee", header: "Platform fee", help: "rateplan.platformFee", numeric: true, cell: (r) => <Money micro={r.feeMicro} /> },
  { key: "minimum", header: "Monthly minimum", help: "rateplan.monthlyMinimum", numeric: true, cell: (r) => <Money micro={r.minimumMicro} /> },
  { key: "exceptions", header: "Model exceptions", help: "rateplan.exceptions", numeric: true, cell: (r) => r.exceptionCount },
  { key: "tiers", header: "Volume tiers", help: "rateplan.tiers", numeric: true, cell: (r) => r.tierCount },
];

export function PlanList() {
  const client = useApi();
  const services = useContext(SessionContext);
  const router = useContext(RouterContext);
  const [state, setState] = useState<RatePlanListState>({ kind: "loading" });
  const [extra, setExtra] = useState<{ plans: readonly RatePlan[]; loadingMore: boolean }>({ plans: [], loadingMore: false });

  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canWrite = session?.can("billing:write") ?? false;

  const load = () => {
    if (!client) return;
    setState({ kind: "loading" });
    setExtra({ plans: [], loadingMore: false });
    void loadRatePlans(client).then(setState);
  };

  const loadMore = () => {
    if (!client || state.kind !== "loaded" || !state.nextCursor) return;
    const cursor = state.nextCursor;
    setExtra((current) => ({ ...current, loadingMore: true }));
    void loadRatePlans(client, cursor).then((next) => {
      if (next.kind === "loaded") {
        setExtra((current) => ({ plans: [...current.plans, ...state.plans], loadingMore: false }));
        setState(next);
      } else {
        setExtra((current) => ({ ...current, loadingMore: false }));
        setState(next);
      }
    });
  };

  useEffect(load, [client]);

  const plans = state.kind === "loaded" ? [...extra.plans, ...state.plans] : [];
  const rows = plans.map(ratePlanRow);
  const newPlan = canWrite ? (
    <button type="button" class="button button-primary" onClick={() => router?.navigate("/rate-plans/new")}>
      <Icon name="plus" small />
      New plan
    </button>
  ) : undefined;

  return (
    <div class="page">
      <PageHeader
        title="Rate plans"
        lede="A rate plan turns a customer's AWS usage into the price on their invoice: a markup, exceptions for particular models, volume discounts, a monthly minimum and a platform fee."
        actions={newPlan}
      />
      {state.kind === "loaded" && rows.length === 0 ? (
        <EmptyState
          title="No rate plans yet"
          lede="Customers are only invoiced once they are billed on a rate plan. Create one, activate it, then assign it on a customer's Billing tab."
          steps={[
            { title: "Create a draft", text: "Name it and set the markup, fee, minimum and any exceptions. Drafts can be changed freely." },
            { title: "Activate it", text: "Activating freezes the plan so every invoice on it stays explainable." },
            { title: "Assign it", text: "On a customer's Billing tab, bill the customer on this plan from a month." },
          ]}
          actions={newPlan}
        />
      ) : (
        <div class="card table-card">
          <DataTable
            caption="Rate plans"
            hideCaption
            columns={COLUMNS}
            rows={rows}
            rowKey={(r) => r.id}
            loading={state.kind === "loading" || extra.loadingMore}
            error={state.kind === "error" ? state.error : null}
            onRetry={load}
            emptyMessage="No rate plans yet."
            nextCursor={state.kind === "loaded" ? state.nextCursor : null}
            onLoadMore={loadMore}
          />
        </div>
      )}
    </div>
  );
}
